import { useCallback, useEffect, useId, useRef, useState } from "react";

import Icon from "~components/Icon";
import classnames from "~utils/classnames/classnames";

import { AccordionProps, BellowProps } from "./types";

const Bellow: React.FC<BellowProps> = ({
  title,
  content,
  variant = "default",
}) => {
  const bodyRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  const idPrefix = useId();
  const headerId = `accordion-header-${idPrefix}`;
  const contentId = `accordion-content-${idPrefix}`;

  const handleToggle = (): void => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleKeyDown = (e: React.KeyboardEvent): void => {
    if (!["Enter", " "].includes(e.key)) {
      return;
    }

    e.preventDefault();
    handleToggle();
  };

  const adjustHeight = useCallback((isOpen: boolean): void => {
    if (!bodyRef.current) {
      return;
    }
    bodyRef.current.style.height = isOpen
      ? `${bodyRef.current.scrollHeight}px`
      : "0px";

    // Manage focusability
    const focusableElements = bodyRef.current.querySelectorAll<HTMLElement>(
      "a, button, input, textarea, select, [tabindex]",
    );
    focusableElements.forEach((el) => {
      el.tabIndex = isOpen ? 0 : -1;
    });
  }, []);

  useEffect(() => {
    adjustHeight(open);
  }, [adjustHeight, open]);

  useEffect(() => {
    const handleResize = (): void => {
      if (!open) {
        return;
      }
      adjustHeight(open);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [adjustHeight, open]);

  return (
    <div
      className={classnames(
        variant === "card"
          ? "border rounded-lg overflow-hidden border-grey-light bg-grey-light transition-colors"
          : "border-b",
        open && variant === "card" && "bg-white !border-grey-border",
      )}
    >
      <div
        className="flex justify-between items-center p-4 cursor-pointer"
        onClick={handleToggle}
        role="button"
        tabIndex={0}
        aria-expanded={open}
        aria-controls={contentId}
        id={headerId}
        onKeyDown={handleKeyDown}
      >
        <h3 className="!text-body-bold">{title}</h3>
        <span
          className={classnames(
            "transform transition-transform duration-300",
            open && "rotate-180",
          )}
        >
          <Icon type="chevronDown" className="[&>svg]:!h-auto" />
        </span>
      </div>
      <div
        role="region"
        aria-labelledby={headerId}
        id={contentId}
        ref={bodyRef}
        className={classnames(
          "overflow-hidden transition-[height] duration-300 h-[0]",
          open ? "visible" : "invisible",
        )}
      >
        <div className="p-4 pt-0 ">{content}</div>
      </div>
    </div>
  );
};

const Accordion: React.FC<AccordionProps> = ({
  items,
  bellowType = "default",
  className,
}) => (
  <div className={className}>
    {items.map((item) => (
      <Bellow
        key={item.id || item.title}
        title={item.title}
        content={item.content}
        variant={bellowType}
      />
    ))}
  </div>
);

export default Accordion;
